import React, { ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Grid } from '@mui/material';
import { Button, Color } from 'vetrf-ui';
import { IconWithTooltip } from 'components/Icon';
import { RemoveModal } from 'components/RemoveModal';
import { useUserDataContext } from 'components/UserDataContext/context';
import { WidgetColor } from 'components/registry/types/api';
import styles from './TableCard.module.scss';
import { ProgressBar } from './ProgressBar';

type TableCardProps = {
  children: ReactNode;
  title: string;
  color: WidgetColor;
  subTitleIconColor?: Color;
  addLink?: string;
  viewLink: string;
  editLink?: string;
  id: string;
  subTitle?: string;
  subTitleTooltip?: string;
  progressStep?: number;
  onDelete?: (id: string) => void;
  withEdit?: boolean;
  withDuplicate?: boolean;
  withCardAccess?: boolean;
  withFileDownload?: boolean;
  fileId?: string;
  withRemove?: boolean;
  subTitleLink?: string;
  indicators?: ReactNode;
  customBtns?: ReactNode;
};

const getColorClass = (color: WidgetColor) => {
  switch (color) {
    case 'green':
      return styles.green;
    case 'red':
      return styles.red;
    case 'blue':
      return styles.blue;
    case 'yellow':
      return styles.yellow;
    case 'grey':
    default:
      return styles.grey;
  }
};

export const TableCard = ({
  children,
  title,
  addLink,
  viewLink,
  editLink,
  color,
  subTitle,
  subTitleTooltip,
  subTitleIconColor = 'green',
  id,
  progressStep,
  onDelete,
  customBtns,
  fileId,
  withEdit = true,
  withRemove = true,
  withDuplicate = true,
  withFileDownload = false,
  withCardAccess = true,
  subTitleLink,
  indicators,
}: TableCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { userData } = useUserDataContext();

  const handleToggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleDelete = () => {
    handleToggleModal();
    onDelete!(id);
  };

  const duplicateLinkParams = useMemo(
    () => ({
      pathname: addLink,
      state: {
        duplicateId: id,
        backLink: viewLink,
      },
    }),
    [id]
  );

  return (
    <Grid item xs={12} lg={6} xl={4}>
      <div className={classNames(styles.cardWrapper, getColorClass(color))}>
        <div className={styles.cardHeader}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <h5>{withCardAccess ? <Link to={viewLink}>{title}</Link> : title}</h5>
            </Grid>
            <Grid item className={styles.toolBar}>
              {progressStep && <ProgressBar step={progressStep} />}
              {indicators}
              {withCardAccess && (
                <Link to={viewLink}>
                  <IconWithTooltip
                    tooltip="Просмотр"
                    color="white"
                    icon="chevron-right"
                    size={110}
                  />
                </Link>
              )}
              {customBtns!}
              {userData && (
                <>
                  {withEdit && (
                    <Link to={editLink || `${viewLink}/edit`}>
                      <IconWithTooltip
                        tooltip="Редактирование"
                        color="white"
                        icon="pencil"
                        size={110}
                      />
                    </Link>
                  )}
                  {withDuplicate && (
                    <Link to={duplicateLinkParams.pathname!} state={duplicateLinkParams.state}>
                      <IconWithTooltip tooltip="Дублировать" color="white" icon="copy" size={110} />
                    </Link>
                  )}
                  {withFileDownload && (
                    <a href={`/files/${fileId}`}>
                      <IconWithTooltip
                        tooltip="Выгрузить"
                        color="white"
                        icon="download"
                        size={110}
                      />
                    </a>
                  )}
                  {withRemove && (
                    <Button
                      icon="trash"
                      iconColor="white"
                      iconSize={110}
                      onlyIcon
                      tooltip="Удаление"
                      onClick={handleToggleModal}
                    />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </div>
        {subTitle && (
          <div className={styles.cardSubHeader}>
            <IconWithTooltip
              tooltip={subTitleTooltip}
              color={subTitleIconColor}
              icon="bookmark"
              size={120}
            />
            {subTitleLink ? (
              <a href={subTitleLink} target="_blank" rel="noreferrer">
                {subTitle}
              </a>
            ) : (
              <span className={styles.subTitle}>{subTitle}</span>
            )}
          </div>
        )}
        <div className={styles.cardBody}>{children}</div>
      </div>
      {isModalOpen && (
        <RemoveModal
          onClose={handleToggleModal}
          onRemove={handleDelete}
          modalContent="Вы уверены, что хотите удалить запись?"
        />
      )}
    </Grid>
  );
};
